import { Notification } from 'models'
import {
  FC,
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'

import { config } from 'config'
import { GlobalStyle } from 'styles/global'
import { Loader } from 'components/loader'
import { Notifications } from 'components/notifications'
import { buildObjects, parsePeople } from 'utils'

type DataContextType = {
  articles: ReturnType<typeof buildObjects>
  people: ReturnType<typeof parsePeople>
  appLocale: typeof config.locale
  notifications: Notification[]
  addNotification: (message: Error | string, type: Notification['type']) => void
  removeNotification: (messageId: string) => void
}

const DataContext = createContext<DataContextType | null>(null)
const spreadsheetId = '1O4O3qaR3FLVCxwn7sdTEUHLDphtt9OVNs0e7w6zOoYg'
const apiKey = 'AIzaSyDliBdIaszCPY0qUrpbRZZyNpBIlzpAous'

export const DataProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const params = new URLSearchParams(window.location.search)
  const appLocale = (params.get('lang') ||
    config.locale) as typeof config.locale
  const [people, setPeople] = useState<DataContextType['people']>([])
  const [articles, setArticles] = useState<ReturnType<typeof buildObjects>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [notifications, setNotifications] = useState<Notification[]>([])

  const addNotification = (
    message: Error | string,
    type: Notification['type'] = 'info'
  ) => {
    setNotifications(m => [
      ...m,
      typeof message === 'string'
        ? { id: Date.now().toString(), type, text: message }
        : { id: Date.now().toString(), type: 'error', text: message.message },
    ])
  }

  const removeNotification = (messageId: string) => {
    setNotifications(m => m.filter(n => n.id !== messageId))
  }

  useEffect(() => {
    Promise.all([
      fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Articles?alt=json&key=${apiKey}`
      ).then(res => res.json()),
      fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/People?alt=json&key=${apiKey}`
      ).then(res => res.json()),
    ])
      .then(([rawArticles, rawPeople]) => {
        setPeople(parsePeople(rawPeople.values))
        setArticles(buildObjects(rawArticles.values))
      })
      .catch(err => {
        console.log('error', err)
      })
      .finally(() => setIsLoading(false))
  }, [])

  return isLoading ? (
    <>
      <GlobalStyle />
      <Loader />
    </>
  ) : (
    <DataContext.Provider
      value={{
        people,
        articles,
        appLocale,
        notifications,
        addNotification,
        removeNotification,
      }}
    >
      {children}

      <Notifications />
    </DataContext.Provider>
  )
}

export const useDataContext = () => {
  const context = useContext(DataContext)
  return context as DataContextType
}
